import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { ComponentProps } from "@ts/components";
import { clsx } from "clsx";
import React, { ChangeEvent } from "react";
import FormLabel from "./FormLabel";

export type FormTextareaInputProps = ComponentProps & {
  className?: string;
  error?: string;
  handleChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  handleInvalid?: (event: ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  maxLength?: number;
  name: string;
  optional?: boolean;
  placeholder?: string;
  placeholderColor?: string;
  required?: boolean;
  requiredFields?: {
    [key: string]: boolean;
  };
  showLength?: boolean;
  value?: string;
};

const Wrapper = (props: any) => (
  <div
    {...props}
    className={clsx("relative w-full px-mini", props.className)}
  />
);

const Textarea = withFocusRing((props: any) => (
  <textarea
    {...props}
    className={clsx(
      props.className,
      "min-h-15 w-full border-b bg-transparent focus:outline-none",
      props.error === "true" && props.placeholderColor === "dark"
        ? "text-grey"
        : "text-primary",
      props.error === "true" ? "border-warning text-warning" : "border-grey",
      props.requiredError && "border-b-2 border-warning text-warning",
    )}
  />
));

const MessageLength = ({ children }: { children: React.ReactNode }) => (
  <span className="bottom-3-6 absolute right-midi text-body">{children}</span>
);

const ErrorMessage = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-micro text-caption text-warning">{children}</p>
);

const FormTextarea = ({
  className,
  error,
  handleChange,
  handleInvalid,
  id,
  label,
  maxLength,
  name,
  optional,
  placeholder,
  placeholderColor,
  required,
  requiredFields,
  showLength,
  value,
  "data-testid": dataTestId,
}: FormTextareaInputProps) => {
  return (
    <Wrapper className={className}>
      <FormLabel
        label={label}
        id={id}
        required={required}
        optional={optional}
        requiredError={requiredFields?.[id]}
      />
      <Textarea
        aria-invalid={requiredFields?.[id]}
        aria-describedby={id}
        data-testid={dataTestId}
        error={error}
        id={id}
        maxLength={maxLength}
        name={name}
        onChange={handleChange}
        onInvalid={handleInvalid}
        placeholder={placeholder}
        placeholderColor={placeholderColor}
        required={required}
        requiredError={requiredFields?.[id]}
        value={value}
      />
      {showLength && <MessageLength>{`${value?.length}/1000`}</MessageLength>}
      {requiredFields?.[id] && (
        <ErrorMessage>This field is required.</ErrorMessage>
      )}
    </Wrapper>
  );
};

export default FormTextarea;
